/**
 * Session-scoped dataset cache for Vybe Agent analytics and charts.
 */

import {
  capRowsForAgent,
  sliceDatasetRows,
} from "./agent-row-slice";

/** Max rows per array field in vybe_dsl tool results sent to the model. */
export const AGENT_TOOL_ROW_CAP = 120;
export const PREVIEW_ROW_COUNT = 25;

const TRIM_ARRAY_KEYS = ["rows", "before", "after", "bars", "events"] as const;
const DEFAULT_TTL_MS = 30 * 60 * 1000;

export type DatasetRow = Record<string, unknown>;

export interface AgentDataset {
  id: string;
  label: string;
  columns: string[];
  rows: DatasetRow[];
  meta?: Record<string, unknown>;
  createdAt: number;
  expiresAt: number;
}

export interface PutDatasetInput {
  label: string;
  columns: string[];
  rows: DatasetRow[];
  meta?: Record<string, unknown>;
}

export interface AgentToolDatasetPayload {
  datasetId: string;
  label: string;
  rowCount: number;
  columns: string[];
  summary?: Record<string, unknown>;
  preview?: DatasetRow[];
  meta?: Record<string, unknown>;
}

const store = new Map<string, AgentDataset>();
let idCounter = 0;

function nextId(): string {
  idCounter += 1;
  return `ds_${Date.now().toString(36)}_${idCounter.toString(36)}`;
}

function pruneExpired(): void {
  const now = Date.now();
  for (const [id, ds] of store) {
    if (ds.expiresAt <= now) store.delete(id);
  }
}

export function putDataset(input: PutDatasetInput): string {
  pruneExpired();
  const id = nextId();
  const now = Date.now();
  store.set(id, {
    id,
    label: input.label,
    columns: [...input.columns],
    rows: input.rows,
    meta: input.meta,
    createdAt: now,
    expiresAt: now + DEFAULT_TTL_MS,
  });
  return id;
}

export function getDataset(datasetId: string): AgentDataset | undefined {
  pruneExpired();
  const ds = store.get(datasetId);
  if (!ds) return undefined;
  if (ds.expiresAt <= Date.now()) {
    store.delete(datasetId);
    return undefined;
  }
  return ds;
}

export function hasDataset(datasetId: string): boolean {
  return getDataset(datasetId) != null;
}

export function clearAllDatasets(): void {
  store.clear();
}

export function toAgentToolPayload(
  datasetId: string,
  opts?: {
    summary?: Record<string, unknown>;
    meta?: Record<string, unknown>;
  },
): AgentToolDatasetPayload {
  const ds = getDataset(datasetId);
  if (!ds) {
    throw new Error(`Dataset not found: ${datasetId}`);
  }

  const payload: AgentToolDatasetPayload = {
    datasetId,
    label: ds.label,
    rowCount: ds.rows.length,
    columns: ds.columns,
    summary: opts?.summary ?? (ds.meta?.summary as Record<string, unknown> | undefined),
    meta: opts?.meta ?? ds.meta,
  };

  if (ds.rows.length > 0) {
    const { rows: previewRows } = sliceDatasetRows(
      ds.rows,
      ds.columns,
      PREVIEW_ROW_COUNT,
    );
    payload.preview = previewRows;
  }

  return payload;
}

/** Last N viewport/dataset rows for agent tool payloads (recent bars when time-ordered). */
export function tailRowsForAgent(
  rows: DatasetRow[],
  columns: string[],
  maxRows: number = AGENT_TOOL_ROW_CAP,
): DatasetRow[] {
  return sliceDatasetRows(rows, columns, maxRows).rows;
}

export function trimDslResultForAgent(value: unknown): unknown {
  if (value == null || typeof value !== "object" || Array.isArray(value)) {
    return value;
  }

  const obj = { ...(value as Record<string, unknown>) };
  const sqlColumns = obj.columns;

  for (const key of TRIM_ARRAY_KEYS) {
    const v = obj[key];
    if (!Array.isArray(v) || v.length <= AGENT_TOOL_ROW_CAP) continue;

    const capped = capRowsForAgent(v, AGENT_TOOL_ROW_CAP, {
      columns: Array.isArray(sqlColumns)
        ? (sqlColumns as string[])
        : undefined,
    });
    obj[key] = capped.rows;
    if (capped.truncated) {
      obj[`${key}Truncated`] = true;
      obj[`${key}Count`] = capped.rowCount;
    }
  }
  return obj;
}
